<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api } from "@/services/api";
type Skill = {
  id: string;
  name: string;
  domain: string;
  description: string;
  prompt_template: string;
  enabled: number;
  updated_at: string;
};
type AgentTool = { id:string; name:string; description:string; input_schema:string; lifecycle_policy:string; handler_key:string; enabled:number; updated_at:string };
const skills = ref<Skill[]>([]);
const tools = ref<AgentTool[]>([]);
const selectedId = ref("brief-confirmation");
const saving = ref(false);
const creating = ref(false);
const selected = computed(
  () => skills.value.find((item) => item.id === selectedId.value) || null,
);
const empty = () => ({
  id: "",
  name: "",
  domain: "general",
  description: "",
  prompt_template: "",
  enabled: 1,
  updated_at: "",
});
const draft = ref<Skill>(empty());
async function load() {
  const [skillResult, toolResult] = await Promise.all([api<{ skills: Skill[] }>("/admin/skills"), api<{ tools: AgentTool[] }>("/admin/tools")]);
  skills.value = skillResult.skills;
  tools.value = toolResult.tools;
  if (!selected.value && skills.value[0]) selectedId.value = skills.value[0].id;
  if (selected.value) draft.value = { ...selected.value };
}
function select(skill: Skill) {
  selectedId.value = skill.id;
  creating.value = false;
  draft.value = { ...skill };
}
function newSkill() {
  creating.value = true;
  selectedId.value = "";
  draft.value = empty();
}
async function save() {
  saving.value = true;
  try {
    const body = {
      name: draft.value.name,
      domain: draft.value.domain,
      description: draft.value.description,
      promptTemplate: draft.value.prompt_template,
      enabled: Boolean(draft.value.enabled),
    };
    if (creating.value)
      await api("/admin/skills", {
        method: "POST",
        body: JSON.stringify({ id: draft.value.id, ...body }),
      });
    else
      await api(`/admin/skills/${draft.value.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    creating.value = false;
    await load();
    selectedId.value = draft.value.id;
  } finally {
    saving.value = false;
  }
}
onMounted(() => {
  void load();
});
</script>
<template>
  <div class="skills">
    <header>
      <div>
        <h1>Agent Skills</h1>
        <p class="muted">
          按业务配置 Agent 的对话方式、信息收集和任务生成规则。
        </p>
      </div>
      <button class="btn btn--primary" @click="newSkill">新建 Skill</button>
    </header>
    <section class="tool-registry"><header><div><h2>可调用接口</h2><p class="muted">接口由后端注册并执行；Skill 只能引用已启用接口。</p></div><span>{{ tools.length }} 个接口</span></header><article v-for="tool in tools" :key="tool.id"><div><b>{{ tool.name }}</b><code>{{ tool.id }}</code><p>{{ tool.description }}</p></div><div class="tool-meta"><span>{{ tool.lifecycle_policy }}</span><small>{{ tool.handler_key }}</small><pre>{{ tool.input_schema }}</pre></div></article></section>
    <div class="layout">
      <aside class="catalog">
        <button
          v-for="skill in skills"
          :key="skill.id"
          class="skill-card"
          :class="{ active: skill.id === selectedId }"
          @click="select(skill)"
        >
          <span
            ><b>{{ skill.name }}</b
            ><small>{{ skill.description }}</small></span
          ><i :class="{ on: skill.enabled }">{{
            skill.enabled ? "启用" : "停用"
          }}</i>
        </button>
      </aside>
      <main v-if="draft" class="editor">
        <header>
          <div>
            <h2>{{ creating ? "新建业务 Skill" : draft.name }}</h2>
            <p class="muted">
              系统工具、确认条件和任务权限锁定；此处配置业务知识与提问方式。
            </p>
          </div>
          <label class="switch"
            ><input
              v-model="draft.enabled"
              :true-value="1"
              :false-value="0"
              type="checkbox"
            />启用</label
          >
        </header>
        <div class="form">
          <label
            >Skill 标识<input
              v-model="draft.id"
              class="input"
              :disabled="!creating"
              placeholder="ai-video-production" /></label
          ><label
            >显示名称<input
              v-model="draft.name"
              class="input"
              placeholder="AI 视频生产" /></label
          ><label
            >业务分类<input
              v-model="draft.domain"
              class="input"
              placeholder="ai_video" /></label
          ><label
            >适用说明<textarea
              v-model="draft.description"
              class="textarea"
              rows="2"
            /></label
          ><label class="full"
            >业务提示词<textarea
              v-model="draft.prompt_template"
              class="textarea"
              rows="13"
              placeholder="告诉 Agent 该领域要问什么、不能假设什么、怎样拆解任务。"
            />
          </label>
        </div>
        <section class="tools">
          <h3>系统工具（只读）</h3>
          <span>读取 Brief 上下文与任务进度</span
          ><span>确认后创建/更新任务草案</span
          ><span>已提交需求创建变更请求</span>
        </section>
        <button class="btn btn--primary" :disabled="saving" @click="save">
          {{ saving ? "保存中..." : "保存 Skill" }}
        </button>
      </main>
    </div>
  </div>
</template>
<style scoped>
.skills {
  padding: 30px;
}
.skills > header,
.editor > header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}
.skills h1,
.skills h2,
.tools h3 {
  margin: 0;
}
.skills > header p,
.editor > header p {
  margin: 6px 0 20px;
}
.tool-registry{margin:0 0 18px;border:1px solid var(--line);background:#fff}.tool-registry header{display:flex;justify-content:space-between;align-items:flex-start;padding:14px 16px;border-bottom:1px solid var(--line)}.tool-registry h2{margin:0;font-size:16px}.tool-registry header p{margin:4px 0 0}.tool-registry article{display:grid;grid-template-columns:minmax(0,1fr) 260px;gap:14px;padding:14px 16px;border-bottom:1px solid var(--line)}.tool-registry article:last-child{border-bottom:0}.tool-registry code{margin-left:8px;color:var(--brand-dark);font-size:12px}.tool-registry p{margin:5px 0 0;color:var(--muted);font-size:13px}.tool-meta{display:grid;gap:5px;justify-items:start}.tool-meta span{padding:2px 5px;background:#eff5ff;color:#2868a9;font-size:12px}.tool-meta small{color:var(--muted)}.tool-meta pre{max-width:100%;margin:0;white-space:pre-wrap;color:var(--muted);font-size:11px}@media(max-width:700px){.tool-registry article{grid-template-columns:1fr}}
.layout {
  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);
  border: 1px solid var(--line);
  background: #fff;
}
.catalog {
  padding: 10px;
  border-right: 1px solid var(--line);
  background: #fbfcfc;
}
.skill-card {
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  border: 1px solid transparent;
  background: transparent;
  text-align: left;
}
.skill-card.active {
  border-color: #f1bd92;
  background: #fff0e7;
}
.skill-card b,
.skill-card small {
  display: block;
}
.skill-card small {
  margin-top: 4px;
  color: var(--muted);
  line-height: 1.35;
}
.skill-card i {
  font-size: 11px;
  font-style: normal;
  color: var(--muted);
}
.skill-card i.on {
  color: var(--success);
}
.editor {
  padding: 24px;
}
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 18px 0;
}
.form label {
  display: grid;
  gap: 6px;
  font-weight: 650;
}
.form .full {
  grid-column: 1/-1;
}
.switch {
  display: flex;
  gap: 6px;
  align-items: center;
}
.tools {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 0 0 16px;
  padding: 13px;
  background: #fff7f1;
}
.tools h3 {
  width: 100%;
  font-size: 14px;
}
.tools span {
  padding: 4px 6px;
  background: #fff;
  font-size: 12px;
}
@media (max-width: 700px) {
  .skills {
    padding: 18px 14px;
  }
  .layout {
    grid-template-columns: 1fr;
  }
  .catalog {
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }
  .editor {
    padding: 18px;
  }
  .form {
    grid-template-columns: 1fr;
  }
}
</style>
